export const MAX_COMPOSER_IMAGES = 4

type ImageValue = File | Blob | string

type ComposerImageAnswer = {
    pending_images?: ImageValue[] | null
    local_image_urls?: string[] | null
    answer_image?: ImageValue | null
    answer_image_url?: string | null
    local_answer_image_url?: string | null
}

export type ComposerImageAppendResult = {
    accepted: File[]
    rejectedCount: number
    patch: {
        pending_images: ImageValue[]
        local_image_urls: string[]
        answer_image: ImageValue | null
        answer_image_url: string | null
        local_answer_image_url: string | null
    }
}

export type ComposerImagePayloadItem = {
    id: string
    kind: 'image'
    label: string
    status: 'succeeded'
    url: string
}

const compact = <T>(values: Array<T | null | undefined>): T[] => values.filter(Boolean) as T[]

export function isImageFile(file: unknown): file is File {
    return (
        !!file &&
        typeof (file as File).type === 'string' &&
        (file as File).type.startsWith('image/')
    )
}

export function composerImageValues(answer: ComposerImageAnswer | null | undefined): ImageValue[] {
    if (Array.isArray(answer?.pending_images) && answer.pending_images.length > 0) {
        return compact(answer.pending_images)
    }
    return answer?.answer_image ? [answer.answer_image] : []
}

export function composerImagePreviewUrls(answer: ComposerImageAnswer | null | undefined): string[] {
    if (Array.isArray(answer?.local_image_urls) && answer.local_image_urls.length > 0) {
        return compact([...answer.local_image_urls])
    }
    return [answer?.local_answer_image_url, answer?.answer_image_url].filter(
        (url, index, urls): url is string => !!url && urls.indexOf(url) === index,
    )
}

export function composerImagePayloadItems(
    answer: ComposerImageAnswer | null | undefined,
): ComposerImagePayloadItem[] {
    return composerImagePreviewUrls(answer).map((url, index) => ({
        id: `composer-image-${index}`,
        kind: 'image',
        label: `Attached image ${index + 1}`,
        status: 'succeeded',
        url,
    }))
}

export function composerImageCount(answer: ComposerImageAnswer | null | undefined): number {
    return Math.max(composerImageValues(answer).length, composerImagePreviewUrls(answer).length)
}

export function appendComposerImages(
    answer: ComposerImageAnswer | null | undefined,
    candidates: File[],
    createObjectUrl: (file: File) => string,
): ComposerImageAppendResult {
    const imageCandidates = candidates.filter(isImageFile)
    const availableSlots = Math.max(MAX_COMPOSER_IMAGES - composerImageCount(answer), 0)
    const accepted = imageCandidates.slice(0, availableSlots)
    const previousImages = composerImageValues(answer)
    const previousUrls = composerImagePreviewUrls(answer)
    const images = [...previousImages, ...accepted]
    const localImageUrls = [...previousUrls, ...accepted.map(createObjectUrl)]

    return {
        accepted,
        rejectedCount: imageCandidates.length - accepted.length,
        patch: {
            pending_images: images,
            local_image_urls: localImageUrls,
            answer_image: images[0] || null,
            answer_image_url: localImageUrls[0] || null,
            local_answer_image_url: localImageUrls[0] || null,
        },
    }
}

export function removeComposerImage(answer: ComposerImageAnswer | null | undefined, index: number) {
    const images = composerImageValues(answer).filter((_, imageIndex) => imageIndex !== index)
    const localImageUrls = composerImagePreviewUrls(answer).filter(
        (_, imageIndex) => imageIndex !== index,
    )

    return {
        pending_images: images,
        local_image_urls: localImageUrls,
        answer_image: images[0] || null,
        answer_image_url: localImageUrls[0] || null,
        local_answer_image_url: localImageUrls[0] || null,
    }
}
